import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProgressStep from 'src/components/ProgressStep/ProgressStep';
import DeliveryMethodChosen from 'src/components/Sell/DeliveryMethodChosen/DeliveryMethodChosen';
import DeliveryMethodForm from 'src/components/Sell/DeliveryMethodForm/DeliveryMethodForm';
import SellFundList from 'src/components/Sell/SellFundList/SellFundList';
import AppTitle from 'src/components/Shared/AppTitle/AppTitle';
import CheckBox from 'src/components/Shared/Checkbox/Checkbox';
import StepCollapsed from 'src/components/Shared/StepCollapsed/StepCollapsed';
import { getIsLoading } from 'src/redux/modules/applicationData/applicationData.selectors';
import {
  getValidationResultLoading,
  getAutoRedemption,
} from 'src/redux/modules/executeRedemption/selectors';
import { executeAutomaticSell } from 'src/redux/modules/executeRedemption/executeRedemption.module';
import {
  CANCEL_TRADE_CONFIRMATION_MODAL,
  openModal,
} from 'src/redux/modules/modals/modals.module';
import { saveRawPrintData } from 'src/redux/modules/printConfirm/printConfirm.module';
import { setProgress } from 'src/redux/modules/progress';
import { getProgressCurrentStep } from 'src/redux/modules/progress/selectors';
import {
  selectSellDay,
  selectSellFund,
  selectSellMonth,
  updateSellFund,
  updateFederalWithholding,
  updateSellUniqueIDs,
} from 'src/redux/modules/sellTransaction/actions';
import { showMoreBanks } from 'src/redux/modules/existingBanks/existingBanks.module';
import { getAutoSellWithholdingFunds } from 'src/redux/modules/sellTransaction/selectors';
import {
  isSalesforceRep,
  isCustomerWebViewEnabled,
  isEmbeddedInIFrame,
} from 'src/redux/modules/authentication/authentication.module.selectors';
import {
  getAutoSellEditFund,
  getIsStep1NextButtonDisabled,
  getSellSelectedFundsWithAmountCount,
  getAutoSellTransactionIsValid,
  getSelectedSellFundsList,
} from 'src/redux/modules/sellTransaction/sellTransaction.selectors';
import { SellFund } from 'src/redux/modules/sellTransaction/types';
import {
  IAS_DELIVERY_DIRECT_DEPOSIT,
  IAS_DELIVERY_MAIL_CHECK,
  UNIT,
} from 'src/settings/settings';
import { scrollElementIntoFullView } from 'src/utils/dom-utils';
import { isAutomaticSellEdit } from 'src/utils/transactions-util';
import { AutoSellFundSummary } from '../AutoSellFundSummary';
import { AutoSellWithholding } from '../AutoSellWithholding';
import { AutoSellReviewPanel } from '../Review';
import { Step1Text, Step2Text, Step3Text, Step4Text } from './TitlesAndCopy';
import { getSelectedBank } from 'src/redux/modules/bankSelection/bankSelection.selectors';
import { getUserAge } from 'src/redux/modules/user/PersonalInformation/personalInformation.selectors';
import {
  getDeliveryMethod,
  getSelectedDeliveryMethod,
} from 'src/redux/modules/sellDeliveryMethod/sellDeliveryMethod.selectors';
import { selectDeliveryMethod } from 'src/redux/modules/sellDeliveryMethod/actions';
import { selectBank } from 'src/redux/modules/bankSelection';
import { getRecurringStartDate } from '../../../redux/modules/shoppingCart/actions';
import { getCartLookup } from '../../../redux/modules/shoppingCart/selectors';
import If from '../../Shared/If/If';
import { resetTransaction } from '../../../redux/modules/shoppingCart/shoppingCart.module';
import { getRecurringPrintData } from '../../../redux/modules/executeRecurringPurchase/executeRecurringPurchase.selectors';
import StepExpanded from '../../Shared/StepExpanded/StepExpanded';
import { getSystematicSelections } from '../../../utils/automatic/automatic-utils.js';
import { ThunkDispatch } from 'redux-thunk';

export const AutoSellSteps: React.FC = () => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const fundingTargetStep = 'funding-target-step';
  const isIFrame: boolean = useSelector(isEmbeddedInIFrame);
  const isSalesforceRepEnabled: boolean = useSelector(isSalesforceRep);
  const customerWebViewEnabled: boolean = useSelector(isCustomerWebViewEnabled);
  const { autoSellLoading } = useSelector(getAutoRedemption);
  const sellAutoTransactionIsValid: boolean = useSelector(
    getAutoSellTransactionIsValid
  );
  const isStep1NextButtonDisabled: boolean = useSelector(
    getIsStep1NextButtonDisabled
  );
  const validatedSellFundsList = useSelector(getAutoSellWithholdingFunds);
  const selectedSellFunds = useSelector(getSelectedSellFundsList);
  const selectedBank = useSelector(getSelectedBank);
  const userAge = useSelector(getUserAge);
  const deliveryMethod = useSelector(getDeliveryMethod);
  const sellWithholdingWarned: boolean = validatedSellFundsList.some(
    (fund) => !fund.nonRetirementLike
  );

  const [isRepSelected, setIsRepSelected] = useState(false);

  //const redemptionPayload = useSelector(getIsStep1NextButtonDisabled);
  const validationResultLoading = useSelector(getValidationResultLoading);
  const currentStep = useSelector(getProgressCurrentStep);
  const SellSelectedFundsWithAmountCount: number = useSelector(
    getSellSelectedFundsWithAmountCount
  );
  const onClickScroll = useCallback(
    (step: number, element: string) => {
      dispatch(setProgress(step));
      scrollElementIntoFullView(element, 15);
    },
    [dispatch]
  );

  const startDates = useSelector(getCartLookup);

  //Edit flow set up
  const sellEditFundList = useSelector(getAutoSellEditFund);
  const selectedDeliveryMethod = useSelector(getSelectedDeliveryMethod);
  const isApplicationLoading = useSelector(getIsLoading);
  const printData = useSelector(getRecurringPrintData);

  const isCustViewDisabledSalesForceEnabled = () => {
    return !customerWebViewEnabled && isSalesforceRepEnabled;
  };

  useEffect(() => {
    if (isAutomaticSellEdit()) {
      const editFund = sellEditFundList[0];
      if (
        selectedDeliveryMethod === '' &&
        editFund.agreementID &&
        editFund.currentYearAmount &&
        !isApplicationLoading
      ) {
        if (
          editFund?.deliveryType?.enumerationName === IAS_DELIVERY_MAIL_CHECK
        ) {
          dispatch(selectDeliveryMethod('Check'));
        } else if (
          editFund?.deliveryType?.enumerationName ===
          IAS_DELIVERY_DIRECT_DEPOSIT
        ) {
          const automaticEditBank = editFund?.bank?.[0];
          dispatch(selectDeliveryMethod('OneTimeAch'));
          dispatch(selectBank(automaticEditBank));
          dispatch(showMoreBanks(true));
        }
        Promise.all([dispatch(selectSellFund(editFund.agreementID))]).then(
          () => {
            const {
              agreementID,
              currentYearAmount,
              transferMonths = [],
              transferDays = [],
            } = editFund;

            dispatch(
              updateSellFund({
                agreementID,
                grossAmount: currentYearAmount,
                grossUnit:
                  UNIT[editFund.disbursementType.enumerationName.toUpperCase()],
              })
            );
            dispatch(selectSellMonth(transferMonths, agreementID));
            dispatch(selectSellDay(transferDays, agreementID));
            dispatch(
              updateSellUniqueIDs(
                editFund.agreementID,
                editFund.electionAgreementUniqueId1,
                editFund.electionAgreementUniqueId2
              )
            );
            dispatch(
              updateFederalWithholding({
                agreementID: editFund.agreementID,
                federalWithholdingApplied: editFund.IRAWithholding !== 0,
                federalWithholdingPercent: editFund.IRAWithholding,
              })
            );
          }
        );
      }
    }
  }, [
    dispatch,
    isApplicationLoading,
    selectedDeliveryMethod,
    sellEditFundList,
  ]);

  return (
    <form className="animated fadeInUp" onSubmit={(e) => e.preventDefault()}>
      <If test={!(isIFrame || isCustViewDisabledSalesForceEnabled())}>
        <AppTitle
          mboxSubtitle="ret-aa-new-auto-sell-subtitle"
          mboxTitle="ret-aa-new-auto-sell-title"
          subtitle={
            <span>
              Set up recurring withdrawals to sell some of your investments.
            </span>
          }
          title="Automatic Sell"
        />
      </If>

      {/* STEP 1 */}
      <ProgressStep
        expanded
        active={currentStep === 1}
        className={fundingTargetStep}
        clickable={currentStep > 1}
        complete={currentStep > 1}
        nextButtonDisabled={isStep1NextButtonDisabled}
        nextButtonLabel={<Step1Text.Button.Next />}
        showCancelLink={!isCustViewDisabledSalesForceEnabled()}
        step={1}
        title={<Step1Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          currentStep > 1 && onClickScroll(1, fundingTargetStep);
        }}
        onNextClick={() => {
          onClickScroll(2, fundingTargetStep);
        }}
      >
        {currentStep === 1 ? (
          <DeliveryMethodForm />
        ) : (
          <DeliveryMethodChosen
            onEditClick={() => {
              onClickScroll(1, fundingTargetStep);
            }}
          />
        )}
      </ProgressStep>

      {/* STEP 2 */}
      <ProgressStep
        expanded
        active={currentStep === 2}
        className="fund-list-step"
        clickable={
          currentStep > 2 || (currentStep !== 2 && sellAutoTransactionIsValid)
        }
        complete={currentStep > 2}
        nextButtonClassName="e2e-step2-verify-submit"
        nextButtonDisabled={!sellAutoTransactionIsValid}
        nextButtonLabel={<Step2Text.Button.Next />}
        nextButtonLoading={validationResultLoading}
        showCancelLink={!isCustViewDisabledSalesForceEnabled()}
        step={2}
        title={<Step2Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          (currentStep > 2 ||
            (currentStep !== 2 && sellAutoTransactionIsValid)) &&
            !isStep1NextButtonDisabled &&
            onClickScroll(2, fundingTargetStep);
        }}
        onNextClick={() => {
          const systematicsSelections =
            getSystematicSelections(selectedSellFunds);
          dispatch(getRecurringStartDate({ systematicsSelections })());
          onClickScroll(3, 'fund-list-step');
        }}
      >
        {currentStep === 2 ? (
          <SellFundList currentStep={currentStep} />
        ) : (
          <AutoSellFundSummary
            clickable={sellAutoTransactionIsValid}
            sellSelectedFundsWithAmountCount={SellSelectedFundsWithAmountCount}
            onEditClick={() => {
              onClickScroll(2, fundingTargetStep);
            }}
          />
        )}
      </ProgressStep>

      {/* STEP 3 */}
      <ProgressStep
        active={currentStep === 3}
        checkbox={
          currentStep === 3 &&
          isSalesforceRepEnabled &&
          !customerWebViewEnabled &&
          sellWithholdingWarned ? (
            <div className="sell-fund-availiblity-check-con">
              {' '}
              <CheckBox
                checked={isRepSelected}
                inline={true}
                onChange={(e) => setIsRepSelected(e)}
              />
              <span className="sell-fund-availiblity-check-text">
                I have read the current{' '}
                <a
                  href="https://home2.troweprice.com/discovery/confluence/display/retailkms/Templates+And+Disclosures#TemplatesAndDisclosures-IRARedemptionDisclosure"
                  rel="noreferrer"
                  target="_blank"
                >
                  Federal Witholding Disclosures
                </a>
                . Note: Disclosure must be read whether federal withholding
                elected or not
              </span>
            </div>
          ) : (
            <></>
          )
        }
        className="tax-step"
        clickable={
          currentStep > 3 || (currentStep !== 3 && sellAutoTransactionIsValid)
        }
        complete={currentStep > 3}
        expanded={currentStep >= 3}
        nextButtonDisabled={
          (sellWithholdingWarned &&
            !isRepSelected &&
            isSalesforceRepEnabled &&
            !customerWebViewEnabled) ||
          !sellAutoTransactionIsValid
        }
        nextButtonLabel={<Step3Text.Button.Next />}
        showCancelLink={!isCustViewDisabledSalesForceEnabled()}
        step={3}
        title={<Step3Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onClickHeader={() => {
          currentStep >= 3 && onClickScroll(3, 'tax-step');
        }}
        onNextClick={() => {
          // todo: fetch start dates
          onClickScroll(4, 'review-step');
        }}
      >
        {currentStep === 3 ? (
          <AutoSellWithholding funds={validatedSellFundsList as SellFund[]} />
        ) : (
          <div
            className={'sell-fund-summary sell-fund-summary-pointer'}
            onClick={() => {
              currentStep >= 3 && onClickScroll(3, 'tax-step');
            }}
          >
            <StepCollapsed>
              <span className="sell-fund-summary-text">
                Change your federal tax withholding
              </span>
              <a
                className="college-branded-link sell-fund-summary-edit-link"
                id="sell-step-2-edit"
              >
                Edit
              </a>
            </StepCollapsed>
          </div>
        )}
      </ProgressStep>

      {/* STEP 4 */}
      <ProgressStep
        active={currentStep === 4}
        className="review-step"
        clickable={false}
        expanded={currentStep === 4}
        nextButtonDisabled={customerWebViewEnabled || autoSellLoading}
        nextButtonLabel={<Step4Text.Button.Next />}
        showCancelLink={!isCustViewDisabledSalesForceEnabled()}
        step={4}
        title={<Step4Text.Title />}
        onCancelClick={() =>
          dispatch(openModal(CANCEL_TRADE_CONFIRMATION_MODAL))
        }
        onNextClick={() => {
          scrollElementIntoFullView('sell-page-container');
          dispatch(saveRawPrintData(printData));
          dispatch(
            executeAutomaticSell({
              selectedFunds: selectedSellFunds,
              selectedDeliveryMethod,
              selectedBank,
              userAge,
            })
          );
          resetTransaction();
        }}
      >
        {currentStep === 4 && (
          <StepExpanded>
            <AutoSellReviewPanel
              deliveryMethod={deliveryMethod}
              funds={validatedSellFundsList as SellFund[]}
              startDates={startDates}
            />
          </StepExpanded>
        )}
      </ProgressStep>
    </form>
  );
};
